import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from "react";

import {
  faAnglesRight,
  faAnglesLeft,
  faChevronDown,
  faChevronUp,
  faGear,
  faQuestionCircle,
  faComment,
  faSquareCheck,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Spinner } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import "./SideBar.scss";

import { usePagePermissions } from "@/context/PagePermissionsContext";
import { useSidebarLayout } from "@/context/SidebarLayoutContext";
import { logger } from "@/utils/logger";

import ClientHub from "../../assets/NavLogos/ClientHub.webp";
import Governance from "../../assets/NavLogos/Governance.webp";
import NiraLogo from "../../assets/NavLogos/NIRALogo.webp";
import NiraLogoLong from "../../assets/NavLogos/NIRALogo_long.webp";
import PManagement from "../../assets/NavLogos/SecurityPosture.webp";
import ThreatMap from "../../assets/NavLogos/ThreatIntelandMap.webp";
import UserGray from "../../assets/NavLogos/user-gray.webp";
import { useAuth } from "../../context/AuthContext";
import { useScreenSize } from "../../hook/useScreenSize";
import { logout } from "../../services/auth";

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggle }) => {
  // Destructure props
  const GOVERNANCE_EXPANDED_STORAGE_KEY = "sidebar.governanceExpanded";
  const SCROLL_POSITION_STORAGE_KEY = "sidebar.scrollPosition";
  const INITIAL_LOGIN_STORAGE_KEY = "sidebar.initialLoginCompleted";
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(isCollapsed); // Initialize from prop
  const [showText, setShowText] = useState<boolean>(!isCollapsed); // Initialize from prop
  const [isProfileOpen, setIsProfileOpen] = useState<boolean>(false); // Track profile dropdown
  const { isMobile, isTablet } = useScreenSize();
  const { sidebarMode, setCollapsed } = useSidebarLayout();
  const [isGovernanceExpanded, setIsGovernanceExpanded] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(GOVERNANCE_EXPANDED_STORAGE_KEY);
      const initialLoginCompleted = localStorage.getItem(INITIAL_LOGIN_STORAGE_KEY);

      // If this is the initial login and screen is mobile/tablet, default to collapsed
      if (!initialLoginCompleted && window.innerWidth <= 1024) {
        return false;
      }

      return stored !== null ? stored === "true" : true; // default open when sidebar is open
    } catch {
      return true;
    }
  }); // Track governance section expansion
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const sidebarNavRef = useRef<HTMLUListElement | null>(null);
  const isInitialRender = useRef(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { username, loading, role } = useAuth();
  const { hasPageAccess } = usePagePermissions();

  const isActive = (path: string): boolean => location.pathname === path;

  useEffect(() => {
    setIsSidebarCollapsed(isCollapsed); // Sync internal state with prop
  }, [isCollapsed]); // Add isCollapsed to dependency array

  useEffect(() => {
    if (!isSidebarCollapsed) {
      const timer = setTimeout(() => setShowText(true), 200);
      return () => clearTimeout(timer);
    } else {
      setShowText(false);
    }
  }, [isSidebarCollapsed]);

  // Save scroll position when scrolling
  useEffect(() => {
    const sidebarNav = sidebarNavRef.current;
    if (!sidebarNav) return;

    const handleScroll = () => {
      try {
        localStorage.setItem(SCROLL_POSITION_STORAGE_KEY, sidebarNav.scrollTop.toString());
      } catch {
        // no-op if localStorage fails
      }
    };

    sidebarNav.addEventListener("scroll", handleScroll);
    return () => sidebarNav.removeEventListener("scroll", handleScroll);
  }, []);

  // Restore scroll position after navigation
  useLayoutEffect(() => {
    const sidebarNav = sidebarNavRef.current;
    if (!sidebarNav) return;

    // Only restore if not the initial render
    if (!isInitialRender.current) {
      try {
        const savedScrollPosition = localStorage.getItem(SCROLL_POSITION_STORAGE_KEY);
        if (savedScrollPosition) {
          sidebarNav.scrollTop = parseInt(savedScrollPosition, 10);
        }
      } catch {
        // no-op if localStorage fails
      }
    }
    // Set flag to false after the first render
    isInitialRender.current = false;
  }, [location.pathname]);

  // Function to Toggle Sidebar
  const toggleSidebar = (): void => onToggle();

  const toggleProfileMenu = (): void => {
    setIsProfileOpen((prev) => !prev);
  };

  const toggleGovernanceSection = (): void => {
    setIsGovernanceExpanded((prev) => !prev);
  };

  const handleGovernanceClick = (): void => {
    if (!isSidebarCollapsed) {
      // Toggle governance section when sidebar is expanded
      setIsGovernanceExpanded((prev) => !prev);
    } else if (isSidebarCollapsed && !isGovernanceExpanded) {
      // Toggle governance and sidebar when sidebar is collapsed and governance section is not expanded
      onToggle();
      setIsGovernanceExpanded((prev) => !prev);
    } else {
      // Simply toggle sidebar when sidebar is collapsed and governance section is expanded
      onToggle();
    }
  };

  // Mark initial login as completed when user first interacts with the sidebar
  useEffect(() => {
    try {
      const initialLoginCompleted = localStorage.getItem(INITIAL_LOGIN_STORAGE_KEY);
      if (!initialLoginCompleted) {
        localStorage.setItem(INITIAL_LOGIN_STORAGE_KEY, "true");
      }
    } catch {
      // no-op
    }
  }, []);

  // Persist Governance expanded/collapsed state so it remains after navigation
  useEffect(() => {
    try {
      localStorage.setItem(GOVERNANCE_EXPANDED_STORAGE_KEY, String(isGovernanceExpanded));
    } catch {
      // no-op
    }
  }, [isGovernanceExpanded]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    }

    if (isProfileOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isProfileOpen]);

  useEffect(() => {
    if (sidebarMode === "overlay" && !isSidebarCollapsed) {
      setCollapsed(true);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const [isLoading] = React.useState(false);

  // Route prefetching map for better UX - prefetch on hover
  // Industry standard: Prefetch routes on hover to enable instant navigation
  const routePrefetchMap: Record<string, () => Promise<any>> = {
    "/executive-dashboard": () => import("../../pages/ExecutiveDashboardPage/ExecutiveDashboard"),
    "/threat-map": () => import("../../pages/ThreatMapPage/ThreatMap"),
    "/posture-management": () => import("../../pages/PostureManagementPage/PostureManagement"),
    "/client-hub": () => import("../../pages/ClientHubPage/ClientHub"),
    "/governance/data-risks": () => import("../../pages/GovernancePage/DataRisks/DataRisks"),
    "/governance/ai-inventory": () => import("../../pages/GovernancePage/AIInventory/AIInventory"),
    "/governance/ai-adoption": () => import("../../pages/GovernancePage/AIAdoption/AIAdoption"),
    "/governance/tprm": () => import("../../pages/GovernancePage/TprmPage/TprmPage"),
    "/nira": () => import("../../pages/AriaPage/Aria"),
    "/profile": () => import("../../pages/ProfilePage/ProfilePage"),
    "/help": () => import("../../pages/HelpPage/HelpPage"),
    "/main": () => import("../../pages/MainPage/MainPage"),
  };

  // Prefetch route on hover for instant navigation
  const handleMouseEnter = useCallback((path: string) => {
    const prefetchFn = routePrefetchMap[path];
    if (prefetchFn) {
      // Prefetch the route chunk in the background
      prefetchFn().catch(() => {
        // Silently fail - prefetching is optional
      });
    }
  }, []);

  // Navigation handler that ensures navigation always works, even during loading states
  // Industry standard: Navigate immediately, show loading on target page
  // Use React.startTransition to mark navigation as non-urgent, ensuring it doesn't block
  // Prevent scroll-to-top behavior on navigation
  const handleNavigation = useCallback(
    (path: string) => {
      // Navigate immediately - don't wait for anything
      // React Router will navigate and show loading state on target page
      // Using startTransition ensures this is non-blocking
      // Use preventScrollReset option to prevent automatic scroll-to-top on navigation
      React.startTransition(() => {
        navigate(path, { preventScrollReset: true });
      });
    },
    [navigate]
  );

  const handleLogout = async () => {
    try {
      await logout();
      // Clear the initial login state when logging out
      try {
        localStorage.removeItem(INITIAL_LOGIN_STORAGE_KEY);
      } catch {
        // no-op
      }
      // Handle successful logout:
      // 1. Redirect to login page (most common)
      // 2. Clear user session data (e.g., using Zustand, useContext)
      // 3. Show a success message
      navigate("/login");
      // Example: Redirect to login page (using react-router-dom, adjust as needed)
      // If you are using next.js, use next/navigation
      // For example:
      // import { useRouter } from 'next/navigation';
      // const router = useRouter();
      // router.push('/login');

      // If you are using react-router-dom
      // import { useNavigate } from 'react-router-dom';
      // const navigate = useNavigate();
      // navigate('/login');
      window.location.href = "/login"; // simplest way to redirect
    } catch (err: any) {
      // Handle logout failure:
      // 1. Show an error message to the user
      logger.error("Logout failed", { error: err, context: "handleLogout" });
    }
  };

  const isOverlay = sidebarMode === "overlay";
  const isOverlayOpen = isOverlay && !isSidebarCollapsed;
  const homePath = "/main";

  return (
    <div className={`wrapper ${isOverlay ? "overlay-mode" : ""}`}>
      {isOverlayOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setCollapsed(true)}
          aria-hidden="true"
        />
      )}
      <aside
        id="sidebar"
        className={`sidebar ${isSidebarCollapsed ? "collapsed" : ""} ${isOverlayOpen ? "overlay-open" : ""}`}
      >
        <div className="toggle-container">
          <button
            className="toggle-btn m-0"
            type="button"
            onClick={() => handleNavigation(homePath)}
            onMouseEnter={() => handleMouseEnter(homePath)}
          >
            {isSidebarCollapsed ? (
              <img src={NiraLogo} alt="Sentry" className="nira-collapsed-logo mt-3 ms-1 me-0" />
            ) : (
              <>
                <img src={NiraLogoLong} alt="Sentry Logo" className="nira mt-3 me-0" />
                <div className="powered-by-text">Powered by: Diem360</div>
              </>
            )}
          </button>
          <button
            className={`arrow-btn ${isSidebarCollapsed ? "collapsed" : ""}`}
            type="button"
            onClick={toggleSidebar}
          >
            <FontAwesomeIcon
              icon={isSidebarCollapsed ? faAnglesRight : faAnglesLeft}
              className="arrow"
            />
          </button>
        </div>
        <div className="separator mt-3 ms-2 visible">
          <span></span>
        </div>

        {/* Navigations */}
        <ul className="sidebar-nav" ref={sidebarNavRef}>
          {hasPageAccess("executive-dashboard", role) && (
            <li
              className={`sidebar-item ${isActive("/executive-dashboard") ? "active" : ""}`}
              onClick={() => handleNavigation("/executive-dashboard")}
              onMouseEnter={() => handleMouseEnter("/executive-dashboard")}
            >
              <button
                className="link-text"
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  width: "100%",
                  textAlign: "left",
                }}
              >
                <FontAwesomeIcon icon={faSquareCheck} className="logo me-3" />
                <span className={showText ? "show-text ms-2" : "hide-text"}>Summary</span>
              </button>
            </li>
          )}
          {/* PARKED THE INCIDENTS PAGE */}
          {/* {(role === "Super Admin" || role === "CSE" || role === "admin") && (
            <li
              className={`sidebar-item ${isActive("/incidents") ? "active" : ""}`}
              onClick={() => handleNavigation("/incidents")}
              onMouseEnter={() => handleMouseEnter("/incidents")}
            >
              <button
                className="link-text"
                type="button"
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  width: "100%",
                  textAlign: "left",
                }}
              >
                <img src={IncidentsGray} alt="Incidents" className="logo me-3" />
                <span className={showText ? "show-text ms-2" : "hide-text"}>Incidents</span>
              </button>
            </li>
          )} */}
          {hasPageAccess("threat-map", role) && (
            <li
              className={`sidebar-item ${isActive("/threat-map") ? "active" : ""}`}
              onClick={() => handleNavigation("/threat-map")}
              onMouseEnter={() => handleMouseEnter("/threat-map")}
            >
              <button
                className="link-text"
                type="button"
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  width: "100%",
                  textAlign: "left",
                }}
              >
                <img src={ThreatMap} alt="Incidents" className="logo me-3" />
                <span className={showText ? "show-text ms-2" : "hide-text"}>Incidents</span>
              </button>
            </li>
          )}
          {hasPageAccess("posture-management", role) && (
            <li
              className={`sidebar-item ${isActive("/posture-management") || location.pathname.startsWith("/posture-management/") ? "active" : ""}`}
              onClick={() => handleNavigation("/posture-management")}
              onMouseEnter={() => handleMouseEnter("/posture-management")}
            >
              <button
                className="link-text"
                type="button"
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  width: "100%",
                  textAlign: "left",
                }}
              >
                <img src={PManagement} alt="Pen Test" className="logo me-3" />
                <span className={showText ? "show-text ms-2" : "hide-text"}>Security Posture</span>
              </button>
            </li>
          )}
          {/*<li className={`sidebar-item ${isActive("/ueba") ? "active" : ""}`}  onClick={() => navigate("/ueba")} style={{ cursor: "pointer" }}>
            <div className='link-text'>
              <img src={UEBAGray} alt="UEBA" className="logo me-3" />
              <span className={showText ? "show-text ms-2" : "hide-text"}>Users and Entity Behavior <br /> Analytics</span>
            </div>
          </li>*/}

          {/* Governance & Awareness Section */}
          {(hasPageAccess("governance/ai-inventory", role) ||
            hasPageAccess("governance/ai-adoption", role) ||
            hasPageAccess("governance/data-risks", role) ||
            hasPageAccess("governance/tprm", role)) &&
            (() => {
              // Check if this is initial login on mobile/tablet
              const initialLoginCompleted = localStorage.getItem(INITIAL_LOGIN_STORAGE_KEY);
              const shouldHideOnInitialLogin = !initialLoginCompleted && (isMobile || isTablet);

              return (
                !shouldHideOnInitialLogin && (
                  <li
                    className={`sidebar-item collapsible ${
                      isActive("/governance/ai-inventory") ||
                      isActive("/governance/data-risks") ||
                      isActive("/governance/ai-adoption") ||
                      isActive("/governance/tprm")
                        ? "active"
                        : ""
                    }`}
                    onClick={handleGovernanceClick}
                  >
                    <button
                      className="link-text"
                      type="button"
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        width: "100%",
                        textAlign: "left",
                      }}
                    >
                      <img src={Governance} alt="Governance" className="logo me-3" />
                      <span className={showText ? "show-text ms-2" : "hide-text"}>Governance</span>
                    </button>
                    {showText && (
                      <button
                        className="expand-icon-container"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleGovernanceSection();
                        }}
                        aria-label={
                          isGovernanceExpanded
                            ? "Collapse Governance section"
                            : "Expand Governance section"
                        }
                      >
                        <FontAwesomeIcon
                          icon={isGovernanceExpanded ? faChevronUp : faChevronDown}
                          className="expand-icon"
                        />
                      </button>
                    )}
                  </li>
                )
              );
            })()}

          {/* Governance Sub-items */}
          {/* NOTE: Governance sub-items must remain in alphabetical order by display name. */}
          {(hasPageAccess("governance/ai-inventory", role) ||
            hasPageAccess("governance/ai-adoption", role) ||
            hasPageAccess("governance/data-risks", role) ||
            hasPageAccess("governance/tprm", role)) &&
            isGovernanceExpanded &&
            showText &&
            (() => {
              // Check if this is initial login on mobile/tablet
              const initialLoginCompleted = localStorage.getItem(INITIAL_LOGIN_STORAGE_KEY);
              const shouldHideOnInitialLogin = !initialLoginCompleted && (isMobile || isTablet);

              return (
                !shouldHideOnInitialLogin && (
                  <>
                    {hasPageAccess("governance/ai-adoption", role) && (
                      <li
                        className={`sidebar-sub-item ${isActive("/governance/ai-adoption") ? "active" : ""}`}
                        onClick={() => handleNavigation("/governance/ai-adoption")}
                        onMouseEnter={() => handleMouseEnter("/governance/ai-adoption")}
                      >
                        <button
                          className="link-text"
                          type="button"
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            width: "100%",
                            textAlign: "left",
                          }}
                        >
                          <span className="sub-item-text">AI Adoption</span>
                        </button>
                      </li>
                    )}
                    {hasPageAccess("governance/data-risks", role) && (
                      <li
                        className={`sidebar-sub-item ${isActive("/governance/data-risks") ? "active" : ""}`}
                        onClick={() => handleNavigation("/governance/data-risks")}
                        onMouseEnter={() => handleMouseEnter("/governance/data-risks")}
                      >
                        <button
                          className="link-text"
                          type="button"
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            width: "100%",
                            textAlign: "left",
                          }}
                        >
                          <span className="sub-item-text">Data Risk</span>
                        </button>
                      </li>
                    )}
                    {hasPageAccess("governance/ai-inventory", role) && (
                      <li
                        className={`sidebar-sub-item ${isActive("/governance/ai-inventory") ? "active" : ""}`}
                        onClick={() => handleNavigation("/governance/ai-inventory")}
                        onMouseEnter={() => handleMouseEnter("/governance/ai-inventory")}
                      >
                        <button
                          className="link-text"
                          type="button"
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            width: "100%",
                            textAlign: "left",
                          }}
                        >
                          <span className="sub-item-text">Gen Ai Risk</span>
                        </button>
                      </li>
                    )}
                    {hasPageAccess("governance/tprm", role) && (
                      <li
                        className={`sidebar-sub-item ${isActive("/governance/tprm") ? "active" : ""}`}
                        onClick={() => handleNavigation("/governance/tprm")}
                        onMouseEnter={() => handleMouseEnter("/governance/tprm")}
                      >
                        <button
                          className="link-text"
                          type="button"
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            width: "100%",
                            textAlign: "left",
                          }}
                        >
                          <span className="sub-item-text">Third Party Risk</span>
                        </button>
                      </li>
                    )}
                    {/* <li className={`sidebar-sub-item ${isActive("/governance/access-review") ? "active" : ""}`}>
                  <button className='link-text' onClick={() => navigate("/governance/access-review")} type="button" style={{ background: 'none', border: 'none', padding: 0, width: '100%', textAlign: 'left' }}>
                    <span className="sub-item-text">User Access</span>
                  </button>
                </li> */}
                  </>
                )
              );
            })()}

          {(hasPageAccess("client-hub/overview", role) ||
            hasPageAccess("client-hub/product-maturity", role) ||
            hasPageAccess("client-hub/roi-insights", role) ||
            hasPageAccess("client-hub/qbr-planning", role)) && (
            <li
              className={`sidebar-item ${
                location.pathname === "/client-hub" || location.pathname.startsWith("/client-hub/")
                  ? "active"
                  : ""
              }`}
              onClick={() => handleNavigation("/client-hub")}
              onMouseEnter={() => handleMouseEnter("/client-hub")}
            >
              <button
                className="link-text"
                type="button"
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  width: "100%",
                  textAlign: "left",
                }}
              >
                <img src={ClientHub} alt="Client Hub" className="logo me-3" />
                <span className={showText ? "show-text ms-2" : "hide-text"}>Client Hub</span>
              </button>
            </li>
          )}

          {hasPageAccess("nira", role) && (
            <li
              className={`sidebar-item ${isActive("/nira") ? "active" : ""}`}
              onClick={() => {
                if (isActive("/nira")) {
                  try {
                    localStorage.removeItem("ariaSelectedChatTitle");
                  } catch { /* intentionally swallowed */ }
                  window.dispatchEvent(new CustomEvent("openNiraAdvisor"));
                } else {
                  try {
                    localStorage.setItem("niraIntent", "greeting");
                    localStorage.removeItem("ariaSelectedChatTitle");
                  } catch { /* intentionally swallowed */ }
                  handleNavigation("/nira");
                }
              }}
              onMouseEnter={() => handleMouseEnter("/nira")}
            >
              <button
                className="link-text"
                type="button"
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  width: "100%",
                  textAlign: "left",
                }}
              >
                <FontAwesomeIcon icon={faComment} className="logo" />
                <span className={showText ? "show-text ms-2" : "hide-text"}>Chat with Nira</span>
              </button>
            </li>
          )}
        </ul>

        {/* Footer */}
        <ul className="sidebar-footer">
          {showText ? (
            // Expanded sidebar layout: Settings and Help side by side
            <div className="footer-actions d-flex justify-content-between align-items-center mb-2">
              {hasPageAccess("profile", role) && (
                <li
                  className={`sidebar-item ${isActive("/profile") ? "active" : ""}`}
                  onClick={() => handleNavigation("/profile")}
                  onMouseEnter={() => handleMouseEnter("/profile")}
                >
                  <button
                    className="link-text d-flex justify-content-center align-items-center"
                    type="button"
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      width: "100%",
                      textAlign: "left",
                    }}
                  >
                    <FontAwesomeIcon icon={faGear} className="logo me-3" />
                    <span className="show-text ms-1">Settings</span>
                  </button>
                </li>
              )}
              {hasPageAccess("help", role) && (
                <li
                  className={`sidebar-item ${isActive("/help") ? "active" : ""}`}
                  onClick={() => handleNavigation("/help")}
                  onMouseEnter={() => handleMouseEnter("/help")}
                >
                  <button
                    className="link-text d-flex justify-content-center align-items-center"
                    type="button"
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      width: "100%",
                      textAlign: "left",
                    }}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="logo me-3" />
                    <span className="show-text ms-1">Help</span>
                  </button>
                </li>
              )}
            </div>
          ) : (
            // Collapsed sidebar layout: Settings and Help stacked vertically
            <>
              {hasPageAccess("profile", role) && (
                <li
                  className={`sidebar-item ${isActive("/profile") ? "active" : ""}`}
                  onClick={() => handleNavigation("/profile")}
                  onMouseEnter={() => handleMouseEnter("/profile")}
                >
                  <button
                    className="link-text"
                    type="button"
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      width: "100%",
                      textAlign: "left",
                    }}
                  >
                    <FontAwesomeIcon icon={faGear} className="logo me-3" />
                    <span className="hide-text">Settings</span>
                  </button>
                </li>
              )}
              {hasPageAccess("help", role) && (
                <li
                  className={`sidebar-item ${isActive("/help") ? "active" : ""}`}
                  onClick={() => handleNavigation("/help")}
                  onMouseEnter={() => handleMouseEnter("/help")}
                >
                  <button
                    className="link-text"
                    type="button"
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      width: "100%",
                      textAlign: "left",
                    }}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="logo me-3" />
                    <span className="hide-text">Help</span>
                  </button>
                </li>
              )}
            </>
          )}
          <li
            className={`sidebar-item ${isProfileOpen ? "active" : ""}`}
            onClick={toggleProfileMenu}
          >
            <button
              className="link-text"
              type="button"
              style={{
                background: "none",
                border: "none",
                padding: 0,
                width: "100%",
                textAlign: "left",
              }}
            >
              <img src={UserGray} alt="User" className="logo me-3" />
              <div className="d-flex flex-column">
                <span className={showText ? "show-text" : "hide-text"}>Logged As: </span>
                {loading ? (
                  <Spinner animation="border" variant="light" size="sm" />
                ) : (
                  <span className={showText ? "show-text acc-name" : "hide-text"}>{username}</span>
                )}
              </div>
            </button>
          </li>
          {/* Profile Dropdown Menu */}
          {isProfileOpen && (
            <div className="profile-dropdown" ref={dropdownRef}>
              <button className="dropdown-item" onClick={handleLogout} disabled={isLoading}>
                Logout
              </button>
            </div>
          )}
        </ul>
      </aside>
    </div>
  );
};

export default Sidebar;
